import React, { useEffect, useState } from 'react';
import {
  FormInputCurrency,
  FormInputDate,
  FormInputNumber,
  FormInputText,
  FormSelect,
  P,
} from '@fashionnovateam/nova-ui-toolkit';
import { EditableCellProps } from '../types';
import { FieldsValidationSchema } from '../../validation';

export function EditableCell({
  worksheetId,
  field,
  value,
  onUpdate,
  onError,
  errors,
  disabled = false,
  inputType = 'text',
  options,
  placeholder,
  endAdornment,
}: EditableCellProps) {
  const [localValue, setLocalValue] = useState<string | number>(value ?? '');

  useEffect(() => {
    setLocalValue(value ?? '');
  }, [value]);

  const validateAndUpdate = (val: string | number) => {
    const normalizedVal = val === '' ? null : val;
    const schema = FieldsValidationSchema[field];
    if (schema) {
      const result = schema.validate(normalizedVal);
      if (result.error) {
        onError(worksheetId, {
          ...errors,
          [field]: {
            isInvalid: true,
            message: result.error.details[0].message,
          },
        });
      } else {
        const newErrors = { ...errors };
        delete newErrors[field];
        onError(worksheetId, newErrors);
      }
    }
    onUpdate({
      worksheet_id: worksheetId,
      [field]: normalizedVal,
    });
  };

  const handleBlur = () => {
    const normalizedLocal = localValue === '' ? null : localValue;
    const normalizedOriginal = value ?? null;
    if (normalizedLocal !== normalizedOriginal) {
      validateAndUpdate(localValue);
    }
  };

  /**
   * Prevents arrow keys and space from bubbling to the DataGrid so the grid
   * doesn't steal focus or scroll when editing (same fix as Helix / AddItemsGrid).
   */
  const stopKeyDownPropagation = (event: React.KeyboardEvent) => {
    event.stopPropagation();
  };

  const error = errors?.[field];
  const hasError = error?.isInvalid;

  if (disabled) {
    let displayValue: string | number = '-';
    if (value !== null && value !== undefined && value !== '') {
      if (inputType === 'currency') {
        displayValue = `$${Number(value).toFixed(2)}`;
      } else {
        displayValue = value;
      }
    }
    return (
      <div className="flex items-center w-full h-10">
        <P size="sm" color="disabled">
          {displayValue}
        </P>
      </div>
    );
  }

  if (inputType === 'select' && options) {
    return (
      <div className="flex items-center w-full h-10">
        <FormSelect
          label=""
          inputId={`${worksheetId}-${field}`}
          options={options}
          value={String(localValue)}
          onChangeValue={(val) => {
            setLocalValue(val);
            validateAndUpdate(val);
          }}
          error={hasError}
          className="w-full [&_select]:h-10"
        />
      </div>
    );
  }

  if (inputType === 'currency') {
    return (
      <div className="flex items-center w-full h-10">
        <FormInputCurrency
          label=""
          inputId={`${worksheetId}-${field}`}
          value={localValue === '' ? undefined : Number(localValue)}
          onChangeValue={(val) => setLocalValue(val ?? '')}
          onBlur={handleBlur}
          onKeyDown={stopKeyDownPropagation}
          placeholder={placeholder}
          error={hasError}
          className="w-full"
        />
      </div>
    );
  }

  if (inputType === 'number') {
    return (
      <div className="flex items-center w-full h-10">
        <FormInputNumber
          label=""
          inputId={`${worksheetId}-${field}`}
          value={localValue === '' ? undefined : Number(localValue)}
          onChangeValue={(val) => setLocalValue(val ?? '')}
          onBlur={handleBlur}
          onKeyDown={stopKeyDownPropagation}
          placeholder={placeholder}
          endAdornment={endAdornment}
          error={hasError}
          min={0}
          className="w-full"
        />
      </div>
    );
  }

  if (inputType === 'date') {
    return (
      <div className="flex items-center w-full h-10">
        <FormInputDate
          label=""
          inputId={`${worksheetId}-${field}`}
          value={String(localValue)}
          onChangeValue={(val) => {
            setLocalValue(val ?? '');
            validateAndUpdate(val ?? '');
          }}
          onKeyDown={stopKeyDownPropagation}
          error={hasError}
          className="w-full"
        />
      </div>
    );
  }

  return (
    <div className="flex items-center w-full h-10">
      <FormInputText
        label=""
        inputId={`${worksheetId}-${field}`}
        value={String(localValue)}
        onChangeValue={(val) => setLocalValue(val)}
        onBlur={handleBlur}
        onKeyDown={stopKeyDownPropagation}
        placeholder={placeholder}
        error={hasError}
        className="w-full"
      />
    </div>
  );
}
